import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { startAuthentication } from "@simplewebauthn/browser";
import AuthService from "../../../api/services/AuthService";
import { alertErrorMessage, alertSuccessMessage } from "../../../customComponents/CustomAlertMessage";
import LoaderHelper from "../../../customComponents/Loading/LoaderHelper";
import { ProfileContext } from "../../../context/ProfileProvider";
import EmailInputWithDomainSuggest from "../../../customComponents/EmailInputWithDomainSuggest";
import "../PasskeyPage/passkey.css";
import "./emailVerification.css";

function maskEmail(email) {
  if (!email) return "";
  const [u, d] = String(email).split("@");
  if (!d) return email;
  if (u.length <= 2) return `${u[0]}***@${d}`;
  return `${u.slice(0, 2)}***${u.slice(-1)}@${d}`;
}

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return "—";
  }
};

const EmailVerification = () => {
  const navigate = useNavigate();
  const profileCtx = useContext(ProfileContext) || {};
  const userDetails = profileCtx.userDetails || {};
  const handleUserDetails = profileCtx.handleUserDetails || (() => { });

  const emailId = userDetails?.emailId || "";
  const mobileNumber = userDetails?.mobileNumber || "";
  const hasEmail = !!emailId;
  const hasMobile = !!mobileNumber;
  const hasGoogleAuth = (userDetails?.["2fa"] || 0) === 2;

  // Flow UI state (same as original)
  const [isChangeWarnOpen, setIsChangeWarnOpen] = useState(false);
  const [isVerifyEmailOpen, setIsVerifyEmailOpen] = useState(false);
  const [isSecurityRiskMethodsOpen, setIsSecurityRiskMethodsOpen] = useState(false);
  const [isChangeFormOpen, setIsChangeFormOpen] = useState(false);
  const [isLearnMoreOpen, setIsLearnMoreOpen] = useState(false);
  const [warnA, setWarnA] = useState(false);
  const [warnB, setWarnB] = useState(false);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);

  // API-driven state
  const [isLoading, setIsLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [newEmail, setNewEmail] = useState("");
  const [newEmailOtp, setNewEmailOtp] = useState("");
  const [isInitiated, setIsInitiated] = useState(false);
  const [passkeySupported, setPasskeySupported] = useState(false);
  const [hasPasskey, setHasPasskey] = useState(false);
  const [passkeyVerified, setPasskeyVerified] = useState(null);

  // Google Authenticator TOTP code (alternative to email OTP for identity verification)
  const [totpCode, setTotpCode] = useState("");

  const currentEmailOtpCode = useMemo(() => otp.join(""), [otp]);
  const isOtpComplete = otp.every((d) => d !== "");
  const isTotpComplete = totpCode.length === 6;
  // User can confirm using EITHER email OTP (6 digits) OR Google Auth code (6 digits)
  const canConfirmVerify = isOtpComplete || isTotpComplete;

  // Fetch passkeys / passkey support
  const checkPasskeySupport = useCallback(async () => {
    if (window.PublicKeyCredential === undefined || typeof window.PublicKeyCredential !== "function") {
      setPasskeySupported(false);
      return false;
    }
    try {
      const available = await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
      setPasskeySupported(!!available);
      return !!available;
    } catch {
      setPasskeySupported(false);
      return false;
    }
  }, []);

  const fetchPasskeys = useCallback(async () => {
    try {
      const result = await AuthService.passkeyGetList();
      if (result?.success && result?.data != null) {
        const d = result.data;
        const n =
          d.count != null ? Number(d.count) : Array.isArray(d.passkeys) ? d.passkeys.length : 0;
        setHasPasskey(n > 0);
      }
    } catch {
      // silent
    }
  }, []);

  useEffect(() => {
    checkPasskeySupport();
    fetchPasskeys();
  }, [checkPasskeySupport, fetchPasskeys]);

  // Resend timer
  useEffect(() => {
    if (resendTimer <= 0) return undefined;
    const t = setTimeout(() => setResendTimer((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendTimer]);

  const handleOtpChange = (idx, value) => {
    const v = String(value ?? "").replace(/\D/g, "").slice(0, 1);
    setOtp((prev) => {
      const next = [...prev];
      next[idx] = v;
      return next;
    });
  };

  const handleOtpInput = (idx, e) => {
    const v = String(e?.target?.value ?? "").replace(/\D/g, "").slice(0, 1);
    handleOtpChange(idx, v);
    if (v && idx < 5) {
      document.getElementById(`ev-otp-${idx + 1}`)?.focus();
    }
  };

  const handleOtpKeyDown = (idx, e) => {
    if (e.key === "Backspace" && !otp[idx] && idx > 0) {
      document.getElementById(`ev-otp-${idx - 1}`)?.focus();
    }
  };

  const handleInputPaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text");
    const digits = String(pasteData || "").replace(/\D/g, "").slice(0, 6).split("");
    if (digits.length > 0) {
      setOtp((prev) => {
        const next = [...prev];
        digits.forEach((d, i) => {
          if (i < 6) next[i] = d;
        });
        return next;
      });
      window.requestAnimationFrame(() => {
        const targetIdx = digits.length === 6 ? 5 : digits.length;
        document.getElementById(`ev-otp-${targetIdx}`)?.focus();
      });
    }
  };

  const attemptSilentPasskeyVerification = async () => {
    if (!hasPasskey || !passkeySupported) return null;
    try {
      const optionsResult = await AuthService.passkeyGetStepUpOptions();
      if (!optionsResult?.success || !optionsResult?.data) return null;
      let credential;
      try {
        credential = await startAuthentication(optionsResult.data);
      } catch {
        return null;
      }
      const verifyResult = await AuthService.passkeyVerifyStepUp(credential);
      if (verifyResult?.success) return verifyResult.data;
      return null;
    } catch {
      return null;
    }
  };

  // ============ Flow handlers ============
  // Step 0: Edit click → open consent
  const openChangeConsent = () => {
    if (!hasEmail) {
      alertErrorMessage("No email linked to your account.");
      return;
    }
    setIsChangeWarnOpen(true);
  };

  // Step 1: Consent confirmed → try silent passkey; else send current email OTP
  const proceedAfterConsent = async () => {
    setIsChangeWarnOpen(false);
    setOtp(["", "", "", "", "", ""]);
    setTotpCode("");
    setNewEmail("");
    setNewEmailOtp("");
    setIsInitiated(false);
    setPasskeyVerified(null);

    // Try silent passkey first
    if (hasPasskey && passkeySupported) {
      try {
        LoaderHelper.loaderStatus(true);
        const vr = await attemptSilentPasskeyVerification();
        if (vr) {
          setPasskeyVerified(vr);
          alertSuccessMessage("Passkey verified");
          // Skip verify OTP step, go straight to change form
          setIsChangeFormOpen(true);
          return;
        }
      } finally {
        LoaderHelper.loaderStatus(false);
      }
    }

    // Send OTP to current email
    try {
      setIsLoading(true);
      LoaderHelper.loaderStatus(true);
      const result = await AuthService.securitySendOtp("email", "change_email");
      if (result?.success) {
        alertSuccessMessage(result?.message || "Verification code sent to your email");
        setResendTimer(60);
        setIsVerifyEmailOpen(true);
        requestAnimationFrame(() => document.getElementById("ev-otp-0")?.focus());
      } else {
        alertErrorMessage(result?.message || "Failed to send verification code");
      }
    } catch (error) {
      alertErrorMessage(error?.message || "Something went wrong");
    } finally {
      setIsLoading(false);
      LoaderHelper.loaderStatus(false);
    }
  };

  // Step 2: Resend OTP for current email
  const handleResendCurrentOtp = async () => {
    if (resendTimer > 0) return;
    try {
      setIsLoading(true);
      LoaderHelper.loaderStatus(true);
      const result = await AuthService.securitySendOtp("email", "change_email");
      if (result?.success) {
        alertSuccessMessage(result?.message || "Verification code resent");
        setResendTimer(60);
      } else {
        alertErrorMessage(result?.message || "Failed to resend code");
      }
    } catch (error) {
      alertErrorMessage(error?.message || "Something went wrong");
    } finally {
      setIsLoading(false);
      LoaderHelper.loaderStatus(false);
    }
  };

  const handlePasteCurrentOtp = async () => {
    try {
      const text = await navigator.clipboard.readText();
      const digits = String(text || "").replace(/\D/g, "").slice(0, 6).split("");
      if (digits.length < 6) return;
      setOtp([digits[0], digits[1], digits[2], digits[3], digits[4], digits[5]]);
      document.getElementById("ev-otp-5")?.focus();
    } catch {
      // silent
    }
  };

  // Step 3: Confirm current email OTP OR Google Authenticator code → open change form
  const confirmCurrentOtp = () => {
    if (!canConfirmVerify) return;
    setIsVerifyEmailOpen(false);
    setIsSecurityRiskMethodsOpen(false);
    setIsChangeFormOpen(true);
  };

  // Step 4: In change form — Get Code → initiate email change (sends OTP to new email)
  const handleGetNewEmailCode = async () => {
    if (!newEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) {
      alertErrorMessage("Please enter a valid new email address.");
      return;
    }
    try {
      setIsLoading(true);
      LoaderHelper.loaderStatus(true);
      const requestData = { newEmail };
      if (passkeyVerified) {
        requestData.passkeyVerified = true;
        requestData.passkeyUserId = passkeyVerified.userId;
      } else if (isTotpComplete) {
        // Google Authenticator verification path
        requestData.tofaCode = totpCode;
      } else {
        // Email OTP verification path
        requestData.currentEmailOtp = currentEmailOtpCode;
      }
      const result = await AuthService.securityEmailChangeInitiate(requestData);
      if (result?.success) {
        alertSuccessMessage(result?.message || "Verification code sent to new email");
        setIsInitiated(true);
        setResendTimer(60);
      } else {
        alertErrorMessage(result?.message || "Failed to initiate email change");
      }
    } catch (error) {
      alertErrorMessage(error?.message || "Something went wrong");
    } finally {
      setIsLoading(false);
      LoaderHelper.loaderStatus(false);
    }
  };

  // Step 5: Submit → complete email change
  const handleSubmitChangeEmail = async () => {
    if (!isInitiated) {
      alertErrorMessage("Please click 'Get Code' to send verification to your new email first.");
      return;
    }
    if (!newEmailOtp || newEmailOtp.length !== 6) {
      alertErrorMessage("Please enter the 6-digit code sent to your new email.");
      return;
    }
    try {
      setIsLoading(true);
      LoaderHelper.loaderStatus(true);
      const result = await AuthService.securityEmailChangeComplete({ newEmailOtp });
      if (result?.success) {
        alertSuccessMessage(result?.message || "Email changed successfully");
        setIsChangeFormOpen(false);
        setOtp(["", "", "", "", "", ""]);
        setNewEmail("");
        setNewEmailOtp("");
        setIsInitiated(false);
        setPasskeyVerified(null);
        handleUserDetails();
        navigate("/user_profile/security", { replace: true });
      } else {
        alertErrorMessage(result?.message || "Failed to change email");
      }
    } catch (error) {
      alertErrorMessage(error?.message || "Something went wrong");
    } finally {
      setIsLoading(false);
      LoaderHelper.loaderStatus(false);
    }
  };

  const availableMethods = useMemo(() => {
    const list = [];
    if (hasPasskey && passkeySupported) {
      list.push({
        id: "passkey",
        label: "Passkey",
        icon: "/images/security/passkey_icon.svg",
        onClick: async () => {
          setIsSecurityRiskMethodsOpen(false);
          LoaderHelper.loaderStatus(true);
          try {
            const vr = await attemptSilentPasskeyVerification();
            if (vr) {
              setPasskeyVerified(vr);
              setIsVerifyEmailOpen(false);
              setOtp(["", "", "", "", "", ""]);
              setTotpCode("");
              alertSuccessMessage("Passkey verified");
              setIsChangeFormOpen(true);
            } else {
              alertErrorMessage("Passkey could not be used. Please use email or authenticator code.");
            }
          } finally {
            LoaderHelper.loaderStatus(false);
          }
        },
      });
    }
    list.push({
      id: "phone",
      label: "Phone Number",
      icon: "/images/security/phone_icon2.svg",
      onClick: () => {
        setIsSecurityRiskMethodsOpen(false);
        setIsVerifyEmailOpen(false);
        setOtp(["", "", "", "", "", ""]);
        setTotpCode("");
        navigate("/user_profile/security/smsVerification");
      },
    });
    return list;
  }, [hasPasskey, passkeySupported, attemptSilentPasskeyVerification, navigate]);

  const maskedEmailDisplay = maskEmail(emailId) || "Not set";
  const maskedNewEmailDisplay = maskEmail(newEmail) || newEmail;
  const addedOn = formatDate(userDetails?.emailVerifiedAt || userDetails?.createdAt);
  const hasAnyOther = hasMobile || hasGoogleAuth || hasPasskey;

  return (
    <main className="ev-page" aria-labelledby="ev-title">
      <nav className="ev-page__crumbs" aria-label="Breadcrumb">
        <ol className="ev-page__crumbList">
          <li className="ev-page__crumbItem">
            <button type="button" className="ev-page__crumbLink" onClick={() => navigate("/user_profile/security")}>
              Security
            </button>
          </li>
          <li className="ev-page__crumbSep" aria-hidden="true">
            ›
          </li>
          <li className="ev-page__crumbItem ev-page__crumbItem--active" aria-current="page">
            Email
          </li>
        </ol>
      </nav>

      <div className="email_verification_lft">

        <h1 id="ev-title" className="ev-page__title">
          Email Verification
        </h1>
        <p className="ev-page__subtitle">
          Verifies user identity through secure email confirmation. Helps protect account access and ensures authenticity.{" "}
          <span className="ev-page__learn" onClick={() => setIsLearnMoreOpen(true)} style={{cursor: 'pointer'}}>Learn More &gt;</span>
        </p>

        <section className="ev-card" aria-label="Current email">
          <div className="ev-card__left">
            <span className="e" aria-hidden="true">
              <img src="/images/email_vector2.svg" alt="" />
            </span>
            <div className="ev-card__text">
              <div className="ev-card__email">{maskedEmailDisplay}</div>
              <div className="ev-card__meta">Added: {addedOn}</div>
            </div>
          </div>
          <button
            type="button"
            className="ev-card__edit"
            aria-label="Change email"
            onClick={openChangeConsent}
            disabled={isLoading}
          >
            <img src="/images/security/edit_icon.svg" alt="" />
          </button>
        </section>

      </div>

      {isChangeWarnOpen ? (
        <div className="ev-modal-overlay" role="presentation">
          <div className="ev-modal" role="dialog" aria-modal="true" aria-labelledby="ev-warn-title">
            <button type="button" className="ev-modal__close" aria-label="Close" onClick={() => setIsChangeWarnOpen(false)}>
              ×
            </button>

            <div className="ev-modal__art" aria-hidden="true">
              <img className="dark_img" src="/images/security/change_email_vector.svg" alt="" />
              <img className="light_img" src="/images/security/change_email_vector_light.svg" alt="" />
            </div>

            <h2 id="ev-warn-title" className="ev-modal__title">
              Change your email?
            </h2>
            <p className="ev-modal__subtitle">
              {hasAnyOther
                ? "Please confirm you understand the security notice before proceeding."
                : "To enhance your account security, please activate at least one additional verification method."}
            </p>

            <div className="ev-warnBox">
              <label className="ev-warnRow">
                <input type="checkbox" checked={warnA} onChange={(e) => setWarnA(e.target.checked)} />
                <span>
                  For enhanced security, withdrawals may be temporarily restricted for up to 24 hours after updating your email
                  address.
                </span>
              </label>
              <label className="ev-warnRow">
                <input type="checkbox" checked={warnB} onChange={(e) => setWarnB(e.target.checked)} />
                <span>Your previous email can&apos;t be reused for 30 days.</span>
              </label>
            </div>

            <div className="ev-modal__actions">
              <button type="button" className="ev-btn ev-btn--ghost" onClick={() => setIsChangeWarnOpen(false)}>
                Cancel
              </button>
              <button
                type="button"
                className={`ev-btn ev-btn--primary ${warnA && warnB ? "ev-btn--enabled" : ""}`}
                disabled={!warnA || !warnB || isLoading}
                onClick={proceedAfterConsent}
              >
                {isLoading ? "Please wait…" : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {isVerifyEmailOpen ? (
        <div className="passkey-modal-overlay" role="presentation">
          <div
            className="passkey-modal passkey-verify-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="passkey-verify-title"
          >
            <button
              type="button"
              className="passkey-modal__close"
              aria-label="Close"
              onClick={() => {
                setIsVerifyEmailOpen(false);
                setIsSecurityRiskMethodsOpen(false);
                setOtp(["", "", "", "", "", ""]);
                setTotpCode("");
                navigate("/user_profile/security", { replace: true });
              }}
            >
              ×
            </button>

            <h2 id="passkey-verify-title" className="passkey-verify-modal__title">
              Verify Your Identity
            </h2>
            <p className="passkey-verify-modal__subtitle">
              The verification code has been sent to your email {maskedEmailDisplay}, valid for 10 minutes.
            </p>

            <div className="passkey-verify-modal__otpRow" aria-label="Verification code">
              {otp.map((digit, idx) => (
                <input
                  // eslint-disable-next-line react/no-array-index-key
                  key={idx}
                  id={`ev-otp-${idx}`}
                  className="passkey-verify-modal__otp"
                  value={digit}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={1}
                  autoComplete={idx === 0 ? "one-time-code" : undefined}
                  aria-label={`Digit ${idx + 1}`}
                  disabled={isTotpComplete}
                  onInput={(e) => handleOtpInput(idx, e)}
                  onChange={(e) => handleOtpChange(idx, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(idx, e)}
                  onPaste={handleInputPaste}
                />
              ))}
            </div>

            <div className="passkey-verify-modal__links">
              <button
                type="button"
                className="passkey-verify-modal__link"
                onClick={handleResendCurrentOtp}
                disabled={resendTimer > 0 || isLoading || isTotpComplete}
              >
                {resendTimer > 0 ? `Resend in ${resendTimer}s` : "Resend"}
              </button>
              <button
                type="button"
                className="passkey-verify-modal__link passkey-verify-modal__link--paste"
                onClick={handlePasteCurrentOtp}
                disabled={isTotpComplete}
              >
                Paste{" "}
                <span aria-hidden="true">
                  <i className="ri-file-copy-fill" />
                </span>
              </button>
            </div>

            {hasGoogleAuth ? (
              <div className="passkey-verify-modal__totpBlock">
                <div className="passkey-verify-modal__or" role="separator">
                  <span className="passkey-verify-modal__orLine" />
                  <span className="passkey-verify-modal__orText">OR</span>
                  <span className="passkey-verify-modal__orLine" />
                </div>
                <label htmlFor="ev-totp" className="passkey-verify-modal__totpLabel">
                  Google Authenticator Code
                </label>
                <input
                  id="ev-totp"
                  className="passkey-verify-modal__totpInput"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="Enter 6-digit code"
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  disabled={isOtpComplete}
                />
              </div>
            ) : null}

            <button
              type="button"
              className="passkey-verify-modal__confirm"
              disabled={!canConfirmVerify || isLoading}
              onClick={confirmCurrentOtp}
            >
              {isLoading ? "Please wait…" : "Confirm"}
            </button>

            <div className="passkey-verify-modal__footerRow">
              {availableMethods.length > 0 ? (
                <button
                  type="button"
                  className="passkey-verify-modal__chooseOther"
                  onClick={() => setIsSecurityRiskMethodsOpen(true)}
                >
                  Choose other verification method
                </button>
              ) : null}
              <button
                type="button"
                className="passkey-verify-modal__unable"
                onClick={() =>
                  navigate("/asset_managemnet/security_verification_unavailable", {
                    state: { preselectMethods: ["email"] },
                  })
                }
              >
                Security verification unavailable?
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {isSecurityRiskMethodsOpen && availableMethods.length > 0 ? (
        <div className="passkey-modal-overlay passkey-modal-overlay--onTop" role="presentation">
          <div
            className="passkey-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ev-security-risk-title"
          >
            <button
              type="button"
              className="passkey-modal__close"
              aria-label="Close"
              onClick={() => setIsSecurityRiskMethodsOpen(false)}
            >
              ×
            </button>
            <div className="passkey-modal__art" aria-hidden="true">
              <img className="dark_img" src="/images/security/security_risk_vector.svg" alt="" />
              <img className="light_img" src="/images/security/security_risk_vector_light.svg" alt="" />
            </div>
            <h2 id="ev-security-risk-title" className="passkey-modal__title">
              Security Risk Warning
            </h2>
            <p className="passkey-modal__subtitle">
              To enhance your account security, please activate at least one additional verification method.
            </p>
            <div className="passkey-modal__list" role="list">
              {availableMethods.map((method) => (
                <button
                  key={method.id}
                  type="button"
                  className="passkey-modal__item"
                  role="listitem"
                  onClick={method.onClick}
                >
                  <span className="passkey-modal__itemLeft">
                    <span className="passkey-modal__itemIcon" aria-hidden="true">
                      <img src={method.icon} alt="" />
                    </span>
                    <span className="passkey-modal__itemText">{method.label}</span>
                  </span>
                  <span className="passkey-modal__chev" aria-hidden="true">
                    ›
                  </span>
                </button>
              ))}
            </div>
            <button
              type="button"
              className="passkey-verify-modal__unable"
              style={{ marginTop: 16 }}
              onClick={() =>
                navigate("/asset_managemnet/security_verification_unavailable", {
                  state: { preselectMethods: ["email"] },
                })
              }
            >
              Security verification unavailable?
            </button>
          </div>
        </div>
      ) : null}

      {isChangeFormOpen ? (
        <div className="ev-modal-overlay" role="presentation">
          <div className="ev-modal ev-change-modal" role="dialog" aria-modal="true" aria-labelledby="ev-change-title">
            <button
              type="button"
              className="ev-modal__close"
              aria-label="Close"
              onClick={() => {
                setIsChangeFormOpen(false);
                setNewEmail("");
                setNewEmailOtp("");
                setIsInitiated(false);
              }}
            >
              ×
            </button>

            <h2 id="ev-change-title" className="ev-change-modal__title">
              Change Email
            </h2>
            <p className="ev-change-modal__subtitle">
              {isInitiated
                ? `Enter the 6-digit Verification code sent to ${maskedNewEmailDisplay}`
                : "Enter your new email and click 'Get Code' to receive a verification code."}
            </p>

            <div className="ev-change-field">
              <label className="ev-change-label">New Email Address</label>
              <EmailInputWithDomainSuggest
                inputClassName="ev-change-input"
                wrapClassName="eids-email-wrap"
                placeholder="you@example.com"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                disabled={isInitiated || isLoading}
              />
            </div>

            <div className="ev-change-field">

              <label className="ev-change-label">Enter Verification Code</label>
              <div className="ev-change-labelRow">
                <input
                  className="ev-change-input"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="123456"
                  value={newEmailOtp}
                  onChange={(e) => setNewEmailOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                />
                <button
                  type="button"
                  className="ev-change-get"
                  onClick={handleGetNewEmailCode}
                  disabled={isLoading || (resendTimer > 0 && isInitiated)}
                >
                  {resendTimer > 0 && isInitiated ? `Resend (${resendTimer}s)` : isInitiated ? "Resend" : "Get Code"}
                </button>
              </div>

            </div>

            <button
              type="button"
              className="ev-change-submit"
              onClick={handleSubmitChangeEmail}
              disabled={isLoading || !isInitiated || newEmailOtp.length !== 6}
            >
              {isLoading ? "Submitting…" : "Submit"}
            </button>
          </div>
        </div>
      ) : null}

      {isLearnMoreOpen ? (
        <div className="ev-modal-overlay" role="presentation">
          <div className="ev-modal" role="dialog" aria-modal="true">
            <button type="button" className="ev-modal__close" aria-label="Close" onClick={() => setIsLearnMoreOpen(false)}>
              ×
            </button>
            <h2 className="ev-modal__title" style={{ marginTop: 20 }}>
              About Email Verification
            </h2>
            <p className="ev-modal__subtitle" style={{ textAlign: "left", marginBottom: 30 }}>
              Email verification is a crucial security measure used to ensure that the email address provided belongs to the user. When you perform sensitive actions—such as changing your password, adding a new withdrawal address, or withdrawing funds—we will send a one-time verification code to this address. This confirms your identity, protects your account from unauthorized access, and maintains the overall integrity of the exchange ecosystem.
            </p>
            <div className="ev-modal__actions">
              <button
                style={{ width: "100%" }}
                type="button"
                className="ev-btn ev-btn--primary ev-btn--enabled"
                onClick={() => setIsLearnMoreOpen(false)}
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </main>
  );
};

export default EmailVerification;
